"use client";

import { motion } from 'framer-motion';
import AnimatedSection from '../AnimatedSection';
import { useTranslation } from 'react-i18next';
import Image from 'next/image';

const TerritorySection = () => {
  const { t } = useTranslation();
  
  return (
    <section className="bg-brand-orange py-20" id="territory">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Left Column - Text Content */}
          <AnimatedSection delay={0.2}>
            <div className="text-white">
              <motion.h2 
                className="text-4xl font-bold mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                {t('territory.title')}
              </motion.h2>
              
              <div className="space-y-6">
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  {t('territory.description1')}
                </motion.p>
                
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  {t('territory.description2')}
                </motion.p>
              </div>
            </div>
          </AnimatedSection>
          
          {/* Right Column - Images */}
          <AnimatedSection delay={0.4}>
            <div className="grid grid-cols-2 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.4, ease: "easeInOut"}}
              >
                <Image 
                  src="/images/territory/img-territorio1.jpg" 
                  alt="Floresta tropical" 
                  width={400}
                  height={300}
                  className="w-full h-48 md:h-64 object-cover rounded-lg"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.4, ease: "easeInOut"}}
              >
                <Image 
                  src="/images/territory/img-territorio2.jpg" 
                  alt="Grãos de café" 
                  width={400}
                  height={300}
                  className="w-full h-48 md:h-64 object-cover rounded-lg"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.4, ease: "easeInOut"}}
              >
                <Image 
                  src="/images/territory/img-territorio3.jpg" 
                  alt="Colheita de café" 
                  width={400}
                  height={300}
                  className="w-full h-48 md:h-64 object-cover rounded-lg"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.4, ease: "easeInOut"}}
              >
                <Image 
                  src="/images/territory/img-territorio4.jpg"
                  alt="Produtor de café" 
                  width={400}
                  height={300}
                  className="w-full h-48 md:h-64 object-cover rounded-lg"
                />
              </motion.div>
            </div>
          </AnimatedSection>
        </div>

        <div className="text-white mt-20">
          <motion.h2 
            className="text-4xl font-bold mb-12 text-center md:text-left"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            {t('territory.impact_title')}
          </motion.h2>
          
          <div className="relative">
            <motion.div 
              className="relative"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <Image 
                src="/images/territory/q-impacto-desk.png" 
                alt="Impact visualization"
                width={1200}
                height={600}
                className="w-full hidden md:block"
              />
              
              {/* Mobile version - Stats in grid */}
              <div className="grid grid-cols-2 gap-6 md:hidden">
                <div className="text-center p-4 bg-white/10 rounded-lg">
                  <p className="text-2xl font-bold">12.172</p>
                  <p className="text-sm">{t('territory.producers')}</p>
                </div>
                <div className="text-center p-4 bg-white/10 rounded-lg">
                  <p className="text-2xl font-bold">65.909 ha</p>
                  <p className="text-sm">{t('territory.production_area')}</p>
                </div>
                <div className="text-center p-4 bg-white/10 rounded-lg">
                  <p className="text-2xl font-bold">342.324</p>
                  <p className="text-sm">{t('territory.population')}</p>
                </div>
                <div className="text-center p-4 bg-white/10 rounded-lg">
                  <p className="text-2xl font-bold">17.657</p>
                  <p className="text-sm">{t('territory.direct_jobs')}</p>
                </div>
                <div className="text-center p-4 bg-white/10 rounded-lg col-span-2">
                  <p className="text-2xl font-bold">1,59 {t('territory.unit')}</p>
                  <p className="text-sm">
                    {t('territory.annual_production')}<br />
                    {t('territory.annual_production_unit')}
                  </p>
                </div>
                <div className="text-center p-4 bg-white/10 rounded-lg col-span-2">
                  <p className="text-2xl font-bold">882.287</p>
                  <p className="text-sm">{t('territory.indirect_jobs')}</p>
                </div>
              </div>
              
              {/* Desktop Stats overlays */}
              <div className="absolute inset-0 hidden md:block">
                <motion.div 
                  className="absolute top-[15%] left-[35%]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 1.2 }}
                >
                  <p className="text-3xl font-bold">12.172</p>
                  <p className="text-sm">{t('territory.producers')}</p>
                </motion.div>

                <motion.div 
                  className="absolute top-[15%] left-[55%]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 1.4 }}
                >
                  <p className="text-3xl font-bold">65.909 ha</p>
                  <p className="text-sm">{t('territory.production_area')}</p>
                </motion.div>
                
                <motion.div 
                  className="absolute top-[15%] left-[75%]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 1.6 }}
                >
                  <p className="text-3xl font-bold">1,59 {t('territory.unit')}</p>
                  <p className="text-sm">
                    {t('territory.annual_production')}<br />
                    {t('territory.annual_production_unit')}
                  </p>
                </motion.div>
                
                <motion.div 
                  className="absolute bottom-[35%] left-[35%]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 1 }}
                >
                  <p className="text-3xl font-bold">342.324</p>
                  <p className="text-sm">{t('territory.population')}</p>
                </motion.div>

                <motion.div 
                  className="absolute bottom-[35%] left-[55%]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 1.8 }}
                >
                  <p className="text-3xl font-bold">17.657</p>
                  <p className="text-sm">{t('territory.direct_jobs')}</p>
                </motion.div>

                <motion.div 
                  className="absolute bottom-[35%] left-[75%]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 2 }}
                >
                  <p className="text-3xl font-bold">882.287</p>
                  <p className="text-sm">{t('territory.indirect_jobs')}</p>
                </motion.div>
              </div>
            </motion.div>

            <motion.p 
              className="text-xs mt-4 text-center md:text-left"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 2.2 }}
            >
              {t('territory.footnote')}
            </motion.p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TerritorySection;
